import React, { useState, useEffect } from "react";
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Autocomplete from "@mui/material/Autocomplete";
import FormControl from "@mui/material/FormControl";
import TextField from "@mui/material/TextField";
import Select from '@mui/material/Select';
import { makeStyles } from "@mui/styles";
import MenuItem from '@mui/material/MenuItem';
import FormLabel from "@mui/material/FormLabel";
import InputLabel from '@mui/material/InputLabel';
import Button from '@mui/material/Button';
import FormControlLabel from '@mui/material/FormControlLabel';
import Auth from "../../../../data/auth-service";
import RadioGroup from '@mui/material/RadioGroup';
import Typography from "@mui/material/Typography";
import Radio from '@mui/material/Radio';
import Checkbox from '@mui/material/Checkbox';
import Validator, {
  useNoisyValidate,
} from "../../../../components/validator";
import {
  cloneDeep as _cloneDeep,
  isEqual as _isEqual,
} from 'lodash';
import GCPService from "../../../../data/gcp-service";

const useStyles = makeStyles((theme) => ({
  paper: {
    padding: theme.spacing(2),
  },
  button: {
    marginRight: theme.spacing(1),
  },
  select: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
    width: '100%',
  },
  chips: {
    display: 'flex',
    flexWrap: 'nowrap',
    minHeight: 36,
  },
  chip: {
    margin: 2,
    background: theme.palette.secondary.main,
    color: '#FFF',
    fontSize: 16,
    '& .MuiChip-deleteIcon': {
      color: 'rgba(255, 255, 255, 0.75)',
    },
  },
  container: {
    border: '1px solid #515151',
    borderRadius: '10px',
    boxSizing: 'border-box',
  }
}));

const FrontendConfig = ({ frontends, addFrontend, updateFrontend, editingFrontend, protocol, region, regionLink, networkLink, networks, onClose, isUpdate, networkType, project }) => {
  const classes = useStyles();
  const initialFrontendState = {
    IPProtocol: protocol,
    allPorts: true,
    allowGlobalAccess: false,
    backendService: "",
    description: "",
    ipVersion: "IPv4",
    isMirroringCollector: true,
    loadBalancingScheme: "INTERNAL",
    name: "",
    region: regionLink,
    subnetwork: "",
    subnetworkName: "",
    subnetworkRange: "",
    onCreateUpdate: true
  };

  const gcpPerms = Auth.permissions.get('GCP');
  const [readonly] = useState(!gcpPerms.write);
  const frontendForm = useNoisyValidate({});
  const [subNetworkList, setSubNetworkList] = useState([]);
  const [frontend, setFrontend] = useState(initialFrontendState);
  const [ogfrontend, setOGFrontend] = useState(initialFrontendState);
  const [change, setChange] = useState(false);
  const [ipError, setIpError] = useState("");
  const [ipAddressType, setIpAddressType] = useState("automatic");
  const subnetIpVersion = frontend.ipVersion.toUpperCase() === "IPV4" ? 'IPV4_ONLY' : frontend.ipVersion.toUpperCase() === "IPV6" ? 'IPV6_ONLY' : 'IPV4_IPV6';
  const filteredSubnetworkList = subNetworkList.filter((s) => s.stackType === subnetIpVersion);
  let subnets = [];
  subnets = networkLink ? networks.filter((network) => network.selfLink === networkLink).map(network => network.subnetworks).flat() : [];
  let filteredSubnets = [];
  filteredSubnets = filteredSubnetworkList ? filteredSubnetworkList.filter((subnet) => subnets.some((s) => s.selfLink === subnet.selfLink)) : [];
  const [isValid, setIsValid] = useState(false);
  const [nameError, setNameError] = useState("");

  const IPv4SegmentFormat = '(?:[0-9]|[1-9][0-9]|1[0-9][0-9]|2[0-4][0-9]|25[0-5])';
  const IPv4AddressFormat = `(${IPv4SegmentFormat}[.]){3}${IPv4SegmentFormat}`;
  const IPv4AddressRegExp = new RegExp(`^${IPv4AddressFormat}$`);
  const nameReg = new RegExp('^[a-z0-9-]+$');
  const isValidIPv4 = (str) => IPv4AddressRegExp.test(str);

  const isIPInSubnet = (ip, subnet) => {
    const ipInt = ip.split('.').reduce((acc, octet) => (acc << 8) | parseInt(octet, 10), 0) >>> 0;
    const [subnetIP, subnetMask] = subnet.split('/');
    const subnetInt = subnetIP.split('.').reduce((acc, octet) => (acc << 8) | parseInt(octet, 10), 0) >>> 0;
    const maskBits = parseInt(subnetMask, 10);
    const mask = (0xFFFFFFFF << (32 - maskBits)) >>> 0;

    return (ipInt & mask) === (subnetInt & mask);
  }
  const regex = '/^[a-z0-9-]+$/';

  const fetchSubnets = async () => {
    try {
      await GCPService.getSubnets(region).then((res) => {
        setSubNetworkList(res.items);
      });
    } catch (error) {
      console.log("Unable to fetch subnets")
    }

  }

  const fetchSharedSubnetworks = async () => {
    try {
      await GCPService.getSharedVPC(project, region).then(res => {
        setSubNetworkList(res.items)
      })
    } catch (error) {
      console.log("unable to fetch shared subnetworks")
    }

  }

  useEffect(() => {
    if (networkType == "within") {
      fetchSubnets();
    } else if (networkType == "shared") {
      fetchSharedSubnetworks();
    }

  }, [networkType, networks]);

  useEffect(() => {
    const valid = (frontend.name.length > 0) &&
      (frontend.subnetworkName.length > 0) &&
      (ipAddressType == 'custom' ? ((frontend.IPAddress.length > 0) && ipError === "") : true);
    setChange(!_isEqual(ogfrontend, frontend));
    setIsValid(valid && !_isEqual(ogfrontend, frontend));
  }, [frontend, isUpdate]);

  useEffect(() => {
    if (editingFrontend) {
      setIpAddressType(editingFrontend.IPAddress ? "custom" : "automatic");
      setFrontend({
        ...editingFrontend,
        IPProtocol: protocol || editingFrontend.IPProtocol,
      });
      setOGFrontend(
        {
          ...editingFrontend,
          IPProtocol: protocol || editingFrontend.IPProtocol,
        }
      )
    } else {
      setFrontend({ ...initialFrontendState, IPProtocol: protocol });
    }
  }, [editingFrontend]);

  const handleName = (e, validID, valid) => {
    const newName = e.target.value;
   
    if(!nameReg.test(newName)) {
     setNameError("Enter lowercases, numbers, and hypens");
    } else {
      setNameError("");
    }

    frontendForm.set({ ...frontendForm.get(), [validID]: valid });

    setFrontend({
      ...frontend,
      name: newName,
    });
  };

  const handleIPAddressChange = (e, field) => {
    let value = e.target.value;

    setIpAddressType(value)
    if (field === "automatic") {
      setFrontend({
        ...frontend,
        IPAddress: ""
      });
    }
  }

  const handleEnablePacketMirror = (event) => {
    const isChecked = event.target.checked;
    setFrontend((prevFrontend) => ({
      ...prevFrontend,
      isMirroringCollector: isChecked,
    }));
  };

  const handleCustomIPChange = (e) => {

    const ipValue = e.target.value;
    setFrontend({ ...frontend, IPAddress: ipValue });

    if (!isValidIPv4(ipValue)) {
      setIpError(`enter IP address in the range ${frontend.subnetworkRange}`);
    } else {
      const ipInRange = isIPInSubnet(ipValue, frontend.subnetworkRange);
      if (!ipInRange) {
        setIpError(`IP address ${ipValue} is not in the range ${frontend.subnetworkRange}`);
      } else {
        setIpError('');
      }
    }
  }

  const handleAutocompletechange = (event, newValue, field) => {
    if (newValue) {
      if (field === 'subnetwork') {
        var fieldName = field + 'Name';
        var fieldRange = field + 'Range';
      }

      frontendForm.set({ ...frontendForm.get(), [field]: newValue.selfLink });

      setFrontend((prevData) => ({
        ...prevData,
        subnetworkName: "",
        [fieldName]: newValue.name,
        [fieldRange]: newValue.ipCidrRange,
        [field]: newValue.selfLink,
      }));
    }
  };

  const handleSubmit = () => {
    if (editingFrontend) {
      updateFrontend({ ...frontend, modified: true });
    } else {
      addFrontend({ ...frontend, create: true, modified: true });
    }
    setFrontend(initialFrontendState);
    setIsValid(false)
  };

  const handleResetFrontend = () => {
    setFrontend(initialFrontendState);
    setIpAddressType("automatic")
    setIsValid(false);
  }

  const handleClose = () => {
    setFrontend(initialFrontendState);
    setIsValid(false);
    if (onClose) onClose();
  };

  return (
    <Grid container xs={12}>
      <Grid item xs={12} >
        {editingFrontend === null ?
          <Typography variant="h6">Add New Frontend</Typography> :
          <Typography variant="h6">Update Frontend</Typography>
        }
      </Grid>
      <Grid item xs={12} mt={2} className={classes.container}>
        <Box p={2}>
          <Grid item container xs={12} spacing={2}>
            <Grid item xs={6}>
            <FormControl fullWidth>
                      <TextField
                        label="Name"
                        aria-label="name"
                        value={frontend.name}
                        onChange={handleName}
                        disabled={editingFrontend === null ? false : frontend?.create ? false : isUpdate}
                        error={nameError !== ""}
                        helperText={`${nameError}`}
                      ></TextField>
                    </FormControl>
            </Grid>
            <Grid item xs={6}>
              <Validator
                valueType="displayName"
                allowEmpty
                fullWidth
                label="Service label (Optional)"
                name="Service label"
                value={frontend.serviceLabel || ""}
                onChange={(e) => setFrontend({ ...frontend, serviceLabel: e.target.value })}
                disabled={editingFrontend === null ? false : frontend?.create ? false : isUpdate}
              />
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel id="IP-version-label">IP version</InputLabel>
                <Select
                  aria-label="ip-version"
                  labelId="IP-version-label"
                  label="IP version"
                  value={frontend.ipVersion.toUpperCase() === "IPV4" ? "IPv4" : "IPv6"}
                  onChange={(e) => {
                    frontend.subnetworkName = "";
                    setFrontend({ ...frontend, ipVersion: e.target.value })
                  }}
                  disabled={editingFrontend === null ? false : frontend?.create ? false : isUpdate}
                >
                  <MenuItem value="IPv4">IPv4</MenuItem>
                  <MenuItem value="IPv6">IPv6</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <Autocomplete
                  value={frontend.subnetworkName}
                  onChange={(event, newValue) =>
                    handleAutocompletechange(
                      event,
                      newValue
                        ? typeof newValue === "object"
                          ? newValue
                          : null
                        : null,
                      "subnetwork"
                    )
                  }
                  options={filteredSubnets}
                  getOptionLabel={(option) =>
                    typeof option === "object"
                      ? `${option.name}`
                      : option
                  }
                  renderInput={(params) => (
                    <TextField {...params} label="Sub Network" />
                  )}
                  disabled={editingFrontend === null ? false : frontend?.create ? false : isUpdate}
                />
              </FormControl>
            </Grid>

            <Grid item xs={6}>
              <TextField
                disabled
                fullWidth
                label="Protocol"
                name="protocol"
                value={protocol || ""}
              />
            </Grid>
            <Grid item xs={6}>
              <FormControl component="fieldset">
                <FormLabel>Global Access</FormLabel>
                <RadioGroup
                  row
                  value={frontend.allowGlobalAccess ? "Enable" : "Disable"}
                  onChange={(e) =>
                    setFrontend({
                      ...frontend,
                      allowGlobalAccess: e.target.value === "Enable",
                    })
                  }
                >
                  <FormControlLabel value="Disable" control={<Radio />} label="Disable" />
                  <FormControlLabel value="Enable" control={<Radio />} label="Enable" />
                </RadioGroup>
              </FormControl>
            </Grid>

            <Grid item container xs={6}>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel id="IP-address-label">IP Address</InputLabel>
                  <Select
                    aria-label="ip-address"
                    labelId="IP-address-label"
                    label="IP address"
                    value={ipAddressType || ""}
                    onChange={(e) => handleIPAddressChange(e, "automatic")}
                    disabled={frontend.subnetworkName.length === 0 || editingFrontend === null ? false : frontend?.create ? false : isUpdate}
                  >
                    <MenuItem value="automatic">Ephemeral (Automatic)</MenuItem>
                    <MenuItem value="custom">Ephemeral (Custom)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              {
                ipAddressType === "custom" && (
                  <Grid item xs={12} mt={2}>
                    <FormControl fullWidth>
                      <TextField
                        label="Custom IP Address"
                        aria-label="custom ip address"
                        value={frontend.IPAddress || ""}
                        onChange={handleCustomIPChange}
                        disabled={editingFrontend === null ? false : frontend?.create ? false : isUpdate}
                        error={ipError !== ""}
                        helperText={`${ipError}`}
                      ></TextField>
                    </FormControl>
                  </Grid>
                )
              }
            </Grid>

            <Grid item xs={12}>
              <FormControl component="fieldset">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={frontend.isMirroringCollector}
                      onChange={handleEnablePacketMirror}
                      name="Packet Mirroring"
                    />
                  }
                  label="Enable this load balancer for Packet Mirroring"
                  disabled={editingFrontend === null ? false : frontend?.create ? false : isUpdate}
                />
              </FormControl>
            </Grid>

            <Grid item xs={12} mt={2} container justifyContent="flex-end" >
              <Box >
                {!readonly && (<Box>
                  {editingFrontend ? (
                    <Button
                      variant="contained"
                      color="secondary"
                      className={classes.button}
                      onClick={handleClose}
                    >
                      Cancel
                    </Button>

                  ) : (
                    <Button
                      variant="contained"
                      color="primary"
                      className={classes.button}
                      onClick={handleResetFrontend}
                    >
                      Clear
                    </Button>
                  )
                  }
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    className={classes.button}
                    onClick={handleSubmit}
                    disabled={(() => {
                      if (!change) {
                        return true;
                      }
                      if (change && isValid) {
                        return false;
                      }

                      return true;
                    })()}
                  >
                    {editingFrontend ? 'Update' : 'Save'}
                  </Button>
                </Box>
                )}
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Grid >
    </Grid >
  );
};

export default FrontendConfig;
